import { useEffect, useState } from "react";

import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, Clock } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";


/**
 * Visual indicator showing session status and expiration time
 * Helpful for debugging and giving users confidence their session is maintained
 */
export const SessionRefreshIndicator = () => {
  const { session } = useAuth();
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);

  useEffect(() => {
    if (!session?.expires_at) {
      setTimeUntilExpiry(null);
      return;
    }

    const updateTimer = () => {
      const expiresAt = session.expires_at! * 1000;
      const now = Date.now();
      const remaining = expiresAt - now;
      setTimeUntilExpiry(remaining > 0 ? remaining : 0);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [session?.expires_at]);

  if (!session || timeUntilExpiry === null) {
    return null;
  }

  const minutes = Math.floor(timeUntilExpiry / 1000 / 60);
  const isExpiringSoon = minutes < 15;
  const expiryDate = new Date(session.expires_at! * 1000);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={isExpiringSoon ? "outline" : "secondary"} 
            className="gap-1.5 cursor-help"
          >
            {isExpiringSoon ? (
              <Clock className="h-3 w-3 text-yellow-600" />
            ) : (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            )}
            <span className="text-xs">
              Session: {minutes}m
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p className="font-medium">Session Status</p>
            <p className="text-xs text-muted-foreground">
              Expires {formatDistanceToNow(expiryDate, { addSuffix: true })}
            </p>
            <p className="text-xs text-muted-foreground">
              Auto-refreshes when you're active
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
